import * as React from "react";
import { useCallback, useEffect, useMemo, useState } from "react";
import { Loader2 } from "lucide-react";
import { useHistory } from "react-router-dom";
import { toast } from "sonner";

import { WizardMarketDataOhlcGateNotice } from "@/components/MarketData/WizardMarketDataOhlcGateNotice";
import { MarketDataUnavailablePanel } from "@/components/MarketData/MarketDataUnavailablePanel";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Field, FieldDescription, FieldLabel } from "@/components/ui/field";
import { cn } from "@/lib/utils";
import { useInstrumentSetupOptional } from "../../../contexts/InstrumentSetupContext";
import { useWizardMarketDataOhlcGate } from "../../../hooks/useWizardMarketDataOhlcGate";
import { DashboardHeaderDateRangePicker } from "../Dashboard/components/DashboardHeaderDateRangePicker";
import { getBacktest } from "../../../services/backtestsApi";
import {
  saveSimulateTradePolicyJob,
  simulateTradePolicyStart,
  waitForSimulateTradePolicyJob,
} from "../../../services/tradePolicyApi";
import type { Backtest } from "../../../types/backtests";
import type { DateRange } from "../../../types/trade";
import type { TradePolicySimulateRequest } from "../../../types/tradePolicy";
import { prepareSimulatePolicyWithDualTimeframes } from "../../../utils/dualCandleTimeframe";
import type { WorkspaceAccountScope } from "../../../types/workspaceAccountScope";
import type {
  WorkspaceCreateContext,
  WorkspaceDetail,
  WorkspaceNode,
} from "../../../types/workspace";
import type {
  DashboardDatePresetTimeframe,
  DashboardDateRangeChangeOptions,
} from "../../../utils/dashboardDateWindowStorage";
import { getActiveDashboardDatePresetId } from "../../../utils/dashboardDateWindowStorage";
import { presentMutationError } from "../../../utils/showApiErrorToast";
import { MarketDataUnresolvedError } from "../../../utils/marketDataResolution";
import { MarketDataUnavailableError } from "../../../utils/marketDataUnavailable";
import type { MarketDataUnavailablePayload } from "../../../types/marketDataUnavailable";
import {
  isMarketDataConnectGateDetail,
  MARKET_DATA_PREFERENCE_SETTINGS_PATH,
} from "../../../utils/marketDataConnectGate";
import { computeRerunSimulationPosition } from "./workspaceLayoutUtils";
import {
  resolveRerunCurrentWindowLabel,
  resolveRerunPrefillDateRange,
  resolveBacktestTradeSymbol,
} from "./workspaceRerunWindow";
import {
  workspacePersistNodeId,
  workspacePersistWorkspaceId,
} from "./workspaceOptimisticReport";

export type WorkspaceRerunSimulationDialogProps = {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  scope: WorkspaceAccountScope;
  workspaceId: string;
  sourceSimulationNode: WorkspaceNode;
  detail: WorkspaceDetail | null;
  lockBrokerAccountCode: string;
  /** Instant canvas placeholder while the job runs. */
  onRerunStarted?: (preview: { title: string }) => void;
  onRerunFailed?: (options?: { keepDialogOpen?: boolean }) => void;
  onSimulationSaved: (backtestId: string) => void;
};

function toLocalDateString(d: Date | null): string | null {
  if (!d) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
}

/**
 * Slim rerun: same policy as the source sim, user only picks a new date window and runs.
 * Dialog chrome hides while the job runs so the canvas shows the report-style loading sequence.
 */
export function WorkspaceRerunSimulationDialog({
  open,
  onOpenChange,
  scope: _scope,
  workspaceId,
  sourceSimulationNode,
  detail,
  lockBrokerAccountCode,
  onRerunStarted,
  onRerunFailed,
  onSimulationSaved,
}: WorkspaceRerunSimulationDialogProps): React.ReactElement {
  const history = useHistory();
  const instrumentSetup = useInstrumentSetupOptional();
  const instrumentSetupOpen = Boolean(instrumentSetup?.isOpen);
  const [loading, setLoading] = useState(false);
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [marketDataUnavailable, setMarketDataUnavailable] =
    useState<MarketDataUnavailablePayload | null>(null);
  const [sourceBacktest, setSourceBacktest] = useState<Backtest | null>(null);
  const [dateRange, setDateRange] = useState<DateRange>({
    startDate: null,
    endDate: null,
  });
  const [selectedPresetId, setSelectedPresetId] =
    useState<DashboardDatePresetTimeframe | null>("all-time");
  // Keep the job mounted after Run while the dialog chrome closes immediately.
  const [hideWhileRunning, setHideWhileRunning] = useState(false);

  const effectiveBrokerAccountCode = useMemo(
    () =>
      lockBrokerAccountCode.trim() ||
      String(sourceBacktest?.broker_account_code ?? "").trim(),
    [lockBrokerAccountCode, sourceBacktest?.broker_account_code]
  );

  const tradeSymbol = useMemo(
    () =>
      resolveBacktestTradeSymbol({
        backtest: sourceBacktest,
        meta: sourceSimulationNode.meta,
      }),
    [sourceBacktest, sourceSimulationNode.meta]
  );

  const wizardMdGate = useWizardMarketDataOhlcGate({
    brokerAccountCode: effectiveBrokerAccountCode,
    symbol: tradeSymbol || undefined,
    enabled: open && Boolean(effectiveBrokerAccountCode.trim()),
  });

  const workspaceContext = useMemo((): WorkspaceCreateContext => {
    const position =
      detail != null
        ? computeRerunSimulationPosition(
            sourceSimulationNode,
            detail.edges,
            detail.nodes
          )
        : { x: 0, y: 0 };
    return {
      workspaceId: workspacePersistWorkspaceId(sourceSimulationNode, workspaceId),
      parentNodeId: workspacePersistNodeId(sourceSimulationNode),
      position,
      meta: {
        isRerun: true,
        rerunOfEntityId: sourceSimulationNode.entityId,
      },
    };
  }, [detail, sourceSimulationNode, workspaceId]);

  useEffect(() => {
    if (!open) {
      setHideWhileRunning(false);
      return;
    }
    let cancelled = false;
    setLoading(true);
    setError(null);
    wizardMdGate.setConnectGate(null);
    setSourceBacktest(null);
    setRunning(false);
    setHideWhileRunning(false);

    // Instant meta-only prefill / label while GET backtest loads.
    const metaPrefill = resolveRerunPrefillDateRange({
      meta: sourceSimulationNode.meta,
    });
    if (metaPrefill) {
      setDateRange(metaPrefill);
      setSelectedPresetId(getActiveDashboardDatePresetId(metaPrefill));
    } else {
      setDateRange({ startDate: null, endDate: null });
      setSelectedPresetId("all-time");
    }

    void (async () => {
      try {
        const backtestId = sourceSimulationNode.entityId.trim();
        if (!backtestId) {
          throw new Error("This simulation has no saved backtest id.");
        }
        const backtest = await getBacktest(backtestId);
        if (cancelled) return;
        if (!backtest.policy_tree) {
          throw new Error(
            "Could not load the policy from this simulation. Open it and save a run first."
          );
        }
        setSourceBacktest(backtest);
        const prefill = resolveRerunPrefillDateRange({
          scope: backtest.scope,
          meta: sourceSimulationNode.meta,
        });
        if (prefill) {
          setDateRange(prefill);
          setSelectedPresetId(getActiveDashboardDatePresetId(prefill));
        } else {
          setDateRange({ startDate: null, endDate: null });
          setSelectedPresetId("all-time");
        }
      } catch (err) {
        if (cancelled) return;
        setError(err instanceof Error ? err.message : "Failed to prepare simulation rerun.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [
    open,
    lockBrokerAccountCode,
    sourceSimulationNode.entityId,
    sourceSimulationNode.meta?.windowLabel,
    sourceSimulationNode.meta?.localStartDate,
    sourceSimulationNode.meta?.localEndDate,
  ]);

  const currentWindowLabel = useMemo(
    () =>
      resolveRerunCurrentWindowLabel({
        scope: sourceBacktest?.scope,
        meta: sourceSimulationNode.meta,
      }),
    [sourceBacktest?.scope, sourceSimulationNode.meta]
  );

  const isAllTime =
    selectedPresetId === "all-time" ||
    (!dateRange.startDate && !dateRange.endDate);

  const canRun =
    Boolean(sourceBacktest?.policy_tree) &&
    (isAllTime || (Boolean(dateRange.startDate) && Boolean(dateRange.endDate))) &&
    !wizardMdGate.runBlocked &&
    !loading &&
    !running;

  const onDateRangeChange = useCallback(
    (next: DateRange, options?: DashboardDateRangeChangeOptions) => {
      setDateRange(next);
      if (options?.presetId) {
        setSelectedPresetId(options.presetId);
        return;
      }
      setSelectedPresetId(getActiveDashboardDatePresetId(next));
    },
    []
  );

  const onRun = useCallback(async () => {
    if (!sourceBacktest?.policy_tree) return;
    const useAllTime =
      selectedPresetId === "all-time" ||
      (!dateRange.startDate && !dateRange.endDate);
    const localStart = useAllTime ? null : toLocalDateString(dateRange.startDate);
    const localEnd = useAllTime ? null : toLocalDateString(dateRange.endDate);
    if (!useAllTime && (!localStart || !localEnd)) {
      toast.warning("Date range required", {
        description: "Pick a start and end date, or choose All time.",
      });
      return;
    }

    const title = useAllTime
      ? "Rerun (all time)"
      : `Rerun ${localStart} → ${localEnd}`;

    setRunning(true);
    setError(null);
    setMarketDataUnavailable(null);

    try {
      const broker =
        lockBrokerAccountCode.trim() ||
        String(sourceBacktest.broker_account_code ?? "").trim();

      if (broker) {
        const gate = await wizardMdGate.checkBeforeRun();
        if (gate) {
          setRunning(false);
          return;
        }
      }

      setHideWhileRunning(true);
      onRerunStarted?.({ title });
      const resultMeta =
        sourceBacktest.result?.meta && typeof sourceBacktest.result.meta === "object"
          ? (sourceBacktest.result.meta as Record<string, unknown>)
          : null;
      const preferredCandle =
        sourceSimulationNode.meta?.candleTimeframe?.trim() ||
        sourceBacktest.scope?.candle_timeframe ||
        (typeof resultMeta?.candle_timeframe === "string"
          ? resultMeta.candle_timeframe.trim()
          : "") ||
        "1m";
      const prepared = prepareSimulatePolicyWithDualTimeframes(sourceBacktest.policy_tree, {
        preferredCandleTimeframe: preferredCandle,
        candleTimeframeLocked: true,
        entryCandleTimeframe:
          sourceSimulationNode.meta?.entryCandleTimeframe ||
          (typeof resultMeta?.entry_candle_timeframe === "string"
            ? resultMeta.entry_candle_timeframe
            : undefined),
        managementCandleTimeframe:
          sourceSimulationNode.meta?.managementCandleTimeframe ||
          (typeof resultMeta?.management_candle_timeframe === "string"
            ? resultMeta.management_candle_timeframe
            : undefined),
      });

      const body: TradePolicySimulateRequest = {
        policy: prepared.policy as TradePolicySimulateRequest["policy"],
        ...(sourceBacktest.policy_schema_version
          ? { policy_row_version: sourceBacktest.policy_schema_version }
          : {}),
        ...prepared.fields,
        simulate_ohlc_fill_missing: false,
        ...(broker ? { broker_account_code: broker } : {}),
        ...(useAllTime
          ? { timeframe: "all-time" }
          : {
              local_start_date: localStart!,
              local_end_date: localEnd!,
            }),
        ...(tradeSymbol ? { symbol: tradeSymbol } : {}),
        include_trade_ids: true,
        limit: sourceBacktest.scope?.limit ?? 5000,
        title,
      };

      const started = await simulateTradePolicyStart(body);
      let jobId: string | null = null;
      let backtestId: string | null = null;

      if (started && typeof started === "object" && "job_id" in started) {
        jobId = String((started as { job_id: string }).job_id);
        // Poll until Redis TTL-ish (~1h). Do not abandon at 5m — save happens after succeed.
        const outcome = await waitForSimulateTradePolicyJob(jobId);
        backtestId = outcome.backtestId ?? null;
      } else if (started && typeof started === "object" && "backtest_id" in started) {
        backtestId = String((started as { backtest_id?: string }).backtest_id ?? "");
      }

      if (!backtestId && jobId) {
        const saved = await saveSimulateTradePolicyJob(jobId, {
          workspaceId: workspaceContext.workspaceId,
          parentNodeId: workspaceContext.parentNodeId,
          ...(workspaceContext.position
            ? { position: workspaceContext.position }
            : {}),
          ...(workspaceContext.meta ? { meta: workspaceContext.meta } : {}),
        });
        if (!saved.ok) {
          throw new Error(saved.detail || "Simulation finished but could not save to the workspace.");
        }
        backtestId = saved.backtestId ?? null;
      } else if (backtestId && jobId) {
        // Already persisted by the job — still attach with rerun meta.
        const saved = await saveSimulateTradePolicyJob(jobId, {
          workspaceId: workspaceContext.workspaceId,
          parentNodeId: workspaceContext.parentNodeId,
          ...(workspaceContext.position
            ? { position: workspaceContext.position }
            : {}),
          ...(workspaceContext.meta ? { meta: workspaceContext.meta } : {}),
        });
        if (saved.ok && saved.backtestId) {
          backtestId = saved.backtestId;
        }
      }

      if (!backtestId) {
        throw new Error("Simulation finished but no backtest id was returned.");
      }

      toast.success("Rerun added to workspace");
      onSimulationSaved(backtestId);
      onOpenChange(false);
    } catch (err) {
      if (err instanceof MarketDataUnavailableError) {
        setMarketDataUnavailable(err.payload);
        wizardMdGate.setConnectGate(null);
        setError(null);
      } else if (err instanceof MarketDataUnresolvedError && isMarketDataConnectGateDetail(err.body)) {
        wizardMdGate.setConnectGate({
          message:
            err.body.message ??
            "Your saved chart data source is not connected. Reconnect your broker or switch source.",
          action: err.body.action,
          connect_options: err.body.connect_options ?? [],
          trade_analytics_available: err.body.trade_analytics_available,
        });
        setMarketDataUnavailable(null);
        setError(null);
      } else {
        presentMutationError(err, "Simulation rerun failed");
        setError(err instanceof Error ? err.message : "Simulation rerun failed.");
        wizardMdGate.setConnectGate(null);
        setMarketDataUnavailable(null);
      }
      onRerunFailed?.({ keepDialogOpen: true });
      setHideWhileRunning(false);
    } finally {
      setRunning(false);
    }
  }, [
    dateRange.endDate,
    dateRange.startDate,
    lockBrokerAccountCode,
    onOpenChange,
    onRerunFailed,
    onRerunStarted,
    onSimulationSaved,
    selectedPresetId,
    sourceBacktest,
    sourceSimulationNode,
    workspaceContext,
    wizardMdGate,
  ]);

  const dialogOpen = open && !hideWhileRunning;
  const reserveMdGateSlot =
    loading || wizardMdGate.scanning || Boolean(wizardMdGate.connectGate);

  return (
    <Dialog
      open={dialogOpen}
      onOpenChange={(next) => {
        // Controlled hide while the job runs must not tear down the host (same as report generate).
        if (!next && hideWhileRunning) return;
        onOpenChange(next);
      }}
    >
      <DialogContent
        data-workspace-rerun-simulation-dialog=""
        forceMount={open ? true : undefined}
        className={cn(
          "max-w-md gap-4 font-[family-name:var(--font-primary)] duration-0 data-open:animate-none data-open:fade-in-0 data-open:zoom-in-100",
          instrumentSetupOpen && "!hidden"
        )}
      >
        <DialogHeader>
          <DialogTitle className="text-[color:var(--brand-tropical-surf)]">
            Rerun simulation
          </DialogTitle>
          <DialogDescription className="text-gray-500 dark:text-sidebar-muted-foreground">
            Same policy filters — pick a date range (or All time). We’ll load trades for that
            window as the baseline, then apply the filtration.
          </DialogDescription>
        </DialogHeader>

        <div className="relative flex flex-col gap-4">
          {loading ? (
            <div
              className="absolute inset-0 z-10 flex items-center justify-center gap-2 rounded-[4px] bg-popover/90 text-muted-foreground"
              aria-busy="true"
              aria-live="polite"
            >
              <Loader2 className="size-5 animate-spin" aria-hidden />
              <span className="text-sm">Loading simulation…</span>
            </div>
          ) : null}

          {reserveMdGateSlot ? (
            <div
              className={wizardMdGate.connectGate ? undefined : "min-h-[9.5rem]"}
              aria-hidden={!wizardMdGate.connectGate}
            >
              {wizardMdGate.connectGate ? (
                <WizardMarketDataOhlcGateNotice
                  gate={wizardMdGate.connectGate}
                  history={history}
                  onOpenMarketDataSetup={() => wizardMdGate.openMarketDataSetup()}
                />
              ) : null}
            </div>
          ) : null}

          {marketDataUnavailable ? (
            <MarketDataUnavailablePanel
              reason={marketDataUnavailable.reason}
              message={marketDataUnavailable.message}
              suggestedCandleTypes={marketDataUnavailable.suggestedCandleTypes}
              earliestAvailable={marketDataUnavailable.earliestAvailable}
              maxCandles={marketDataUnavailable.maxCandles}
              limitBasis={marketDataUnavailable.limitBasis}
              tradeAnalyticsAvailable={marketDataUnavailable.tradeAnalyticsAvailable}
              onChangeDataSource={() => history.push(MARKET_DATA_PREFERENCE_SETTINGS_PATH)}
            />
          ) : null}

          {error ? (
            <Alert>
              <AlertDescription className="text-[color:var(--brand-glacial-red)]">
                {error}
              </AlertDescription>
            </Alert>
          ) : null}

          <Field className={loading ? "pointer-events-none opacity-60" : undefined}>
            <FieldLabel className="text-[color:var(--brand-tropical-surf)]">
              Date range
            </FieldLabel>
            <FieldDescription className="text-gray-500 dark:text-sidebar-muted-foreground">
              Trades in this window become the baseline for the rerun.
              {currentWindowLabel ? (
                <>
                  {" "}
                  Current window: <span className="text-foreground">{currentWindowLabel}</span>.
                </>
              ) : null}
            </FieldDescription>
            <div data-workspace-rerun-date-range="" className="mt-2 w-full min-w-0">
              <DashboardHeaderDateRangePicker
                dateRange={dateRange}
                selectedPresetId={selectedPresetId}
                onDateRangeChange={onDateRangeChange}
                className="w-full"
                triggerSurface="field"
              />
            </div>
          </Field>
        </div>

        <DialogFooter className="gap-2 sm:gap-2">
          <Button
            type="button"
            variant="outline"
            data-font="primary"
            disabled={running}
            onClick={() => onOpenChange(false)}
          >
            Cancel
          </Button>
          <Button
            type="button"
            variant="gradient"
            data-font="primary"
            disabled={!canRun}
            onClick={() => void onRun()}
          >
            {running ? <Loader2 className="size-4 animate-spin" /> : null}
            {running ? "Running…" : "Run"}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
